import React, { useMemo, useState } from "react";

import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";

import Dialog from "@busbud/design-system-components/dist/Dialog/Dialog";
import DialogContent from "@busbud/design-system-components/dist/DialogContent/DialogContent";
import DialogTitle from "@busbud/design-system-components/dist/DialogTitle/DialogTitle";
import Divider from "@busbud/design-system-components/dist/Divider";
import AppleMD from "@busbud/design-system-components/dist/Icons/Apple/MD";
import GoogleMD from "@busbud/design-system-components/dist/Icons/Google/MD";
import TicketsMD from "@busbud/design-system-components/dist/Icons/Tickets/MD";
import Label from "@busbud/design-system-components/dist/Label/Label";
import type { ResponsiveColorProp } from "@busbud/design-system-components/dist/system/palette";
import Tab from "@busbud/design-system-components/dist/Tab/Tab";
import Tabs from "@busbud/design-system-components/dist/Tabs/Tabs";

import { useAppContext } from "@app/helpers/hooks";
import { clickedDownloadTicketsTabs } from "@app/tracking/account-tracking";
import type { NapiV2Ticket } from "@app/types/napi-v2-tickets-types";

import type { Dispatch } from "@reduxjs/toolkit";

import { DownloadTicketsType } from "./download-tickets-type";
import { useStyles } from "./download-tickets.styles";
import { closeModal } from "../../../store/state/modals";

interface Props {
  tickets: NapiV2Ticket[];
}

interface ReduxProps {
  closeModal: () => void;
}

const getIconColor = (is_selected: boolean): ResponsiveColorProp =>
  is_selected ? "component.tab.text.primary.selected" : "icon.primary.opaque";

const TabsType = {
  pdf: "pdf",
  apple_wallet: "apple_wallet",
  google_pay_ticket: "google_pay_ticket"
} as const;

const DownloadTicketsComponent: React.FC<ReduxProps & Props> = ({
  tickets,
  closeModal
}) => {
  const { translator, tracker } = useAppContext();
  const [ticket_type, setTicketTypeTab] = useState("pdf");
  const { classes } = useStyles();
  const tickets_with_urls = useMemo(
    () => tickets.filter(ticket => !!ticket.urls),
    [tickets]
  );
  const has_apple_tickets = useMemo(
    () => tickets_with_urls.every(({ urls }) => urls?.pkpass),
    [tickets_with_urls]
  );
  const has_google_tickets = useMemo(
    () => tickets_with_urls.every(({ urls }) => urls?.transit_object_jwt),
    [tickets_with_urls]
  );
  const has_pdf_tickets = useMemo(
    () => tickets_with_urls.some(({ urls }) => urls?.pdf),
    [tickets_with_urls]
  );

  if (!has_apple_tickets && !has_google_tickets && !has_pdf_tickets) {
    return null;
  }

  const handleTabChange = (_e: unknown, value: string) => {
    tracker.track(clickedDownloadTicketsTabs(value));
    setTicketTypeTab(value);
  };

  return (
    <Dialog
      open={true}
      maxWidth="sm"
      fullWidth={true}
      onClose={() => closeModal()}
      // We set a lower z-index than the 1300 default because the Add to Google Wallet modal has a z-index lower than 1300
      // and we want to make sure that the Add to Google Wallet modal is on top of this modal.
      // Also this cannot be set inside a className because this value is set inside `style` prop by default and has precedence over className.
      style={{ zIndex: 1000 }}
    >
      <DialogTitle>
        <Label size="lg" fontWeight="bold">
          {translator.t("!download-tickets.title")}
        </Label>
      </DialogTitle>
      <Divider />
      <Tabs
        orientation="horizontal"
        // TODO - after #657 is merged, set variant back to `fullWidth`
        variant="scrollable"
        value={ticket_type}
        onChange={handleTabChange}
      >
        {!!has_pdf_tickets && (
          <Tab
            label={translator.t("!download-tickets.pdf")}
            value={TabsType.pdf}
            icon={
              <TicketsMD color={getIconColor(ticket_type === TabsType.pdf)} />
            }
            iconDirection="horizontal"
          />
        )}
        {!!has_apple_tickets && (
          <Tab
            label={translator.t("!download-tickets.apple-wallet")}
            value={TabsType.apple_wallet}
            icon={
              <AppleMD
                color={getIconColor(ticket_type === TabsType.apple_wallet)}
              />
            }
            iconDirection="horizontal"
          />
        )}
        {!!has_google_tickets && (
          <Tab
            label={translator.t("!download-tickets.google-pay-pass")}
            value={TabsType.google_pay_ticket}
            icon={
              <GoogleMD
                color={getIconColor(ticket_type === TabsType.google_pay_ticket)}
              />
            }
            iconDirection="horizontal"
          />
        )}
      </Tabs>
      <DialogContent className={classes.content}>
        <DownloadTicketsType
          ticket_type={ticket_type}
          tickets={tickets_with_urls}
        />
      </DialogContent>
    </Dialog>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({ closeModal }, dispatch);

export const DownloadTickets = connect(
  () => ({}),
  mapDispatchToProps
)(DownloadTicketsComponent);
